import React, { useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import moment from 'moment-timezone'
import { Button, Dropdown, Grid, Icon, Segment, Table } from 'semantic-ui-react'
import LoadingSpinner from '../../LoadingSpinner'
import {
    LOAD_AI_USAGE_COST_SUMMARY_QUERY,
    LOAD_AI_USAGE_COST_TEAM_PROJECTS_QUERY,
    LOAD_AI_USAGE_COST_PROJECT_RECORDS_QUERY,
    LOAD_AI_USAGE_COST_DAILY_QUERY,
    LOAD_AI_USAGE_COST_DAY_TEAMS_QUERY,
} from '../../../graphql/admin'

const PACIFIC_TIMEZONE = 'America/Los_Angeles'

const formatMonthLabel = (month) => moment.tz(month, 'YYYY-MM', PACIFIC_TIMEZONE).format('MMMM YYYY')

const formatDayLabel = (date) => moment.tz(date, 'YYYY-MM-DD', PACIFIC_TIMEZONE).format('ddd, MMM D')

const formatTokens = (value) => Number(value || 0).toLocaleString()

const formatCost = (value) => `$${Number(value || 0).toFixed(2)}`

const formatDate = (value) => moment.tz(value, PACIFIC_TIMEZONE).format('MMM D, YYYY h:mm A z')

const ExpandButton = ({ expanded, onClick }) => (
    <Button
        compact
        icon
        basic
        size="mini"
        onClick={onClick}
    >
        <Icon name={expanded ? 'chevron down' : 'chevron right'} />
    </Button>
)

const UsageRecordsTable = ({ month, day, teamId, projectId }) => {
    const { loading, data, error } = useQuery(
        LOAD_AI_USAGE_COST_PROJECT_RECORDS_QUERY,
        { variables: { month, day, teamId, projectId } }
    )

    if (loading) return <LoadingSpinner />
    if (error) {
        return <div>Error loading AI usage records: {error.message}</div>
    }

    const records = data?.adminPortal?.aiUsageCostProjectRecords || []

    return (
        <Table compact celled size="small">
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>Usage</Table.HeaderCell>
                    <Table.HeaderCell>User</Table.HeaderCell>
                    <Table.HeaderCell>Input Tokens</Table.HeaderCell>
                    <Table.HeaderCell>Output Tokens</Table.HeaderCell>
                    <Table.HeaderCell>Thinking Tokens</Table.HeaderCell>
                    <Table.HeaderCell>Cost</Table.HeaderCell>
                    <Table.HeaderCell>Date</Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {records.length === 0 && (
                    <Table.Row>
                        <Table.Cell colSpan={7}>No AI usage recorded.</Table.Cell>
                    </Table.Row>
                )}
                {records.map((record) => (
                    <Table.Row key={record.id}>
                        <Table.Cell>{record.name}</Table.Cell>
                        <Table.Cell>{record.userName}</Table.Cell>
                        <Table.Cell>{formatTokens(record.promptTokens)}</Table.Cell>
                        <Table.Cell>{formatTokens(record.candidatesTokens)}</Table.Cell>
                        <Table.Cell>{formatTokens(record.thoughtsTokens)}</Table.Cell>
                        <Table.Cell>{formatCost(record.cost)}</Table.Cell>
                        <Table.Cell>{formatDate(record.createdAt)}</Table.Cell>
                    </Table.Row>
                ))}
            </Table.Body>
        </Table>
    )
}

const ProjectRow = ({ project, month, day, teamId }) => {
    const [expanded, setExpanded] = useState(false)

    return (
        <React.Fragment>
            <Table.Row>
                <Table.Cell collapsing>
                    <ExpandButton
                        expanded={expanded}
                        onClick={() => setExpanded(!expanded)}
                    />
                </Table.Cell>
                <Table.Cell>{project.projectTitle}</Table.Cell>
                <Table.Cell>{formatTokens(project.promptTokens)}</Table.Cell>
                <Table.Cell>{formatTokens(project.candidatesTokens)}</Table.Cell>
                <Table.Cell>{formatTokens(project.thoughtsTokens)}</Table.Cell>
                <Table.Cell>{formatCost(project.cost)}</Table.Cell>
            </Table.Row>
            {expanded && (
                <Table.Row>
                    <Table.Cell />
                    <Table.Cell colSpan={5}>
                        <UsageRecordsTable
                            month={month}
                            day={day}
                            teamId={teamId}
                            projectId={project.projectId}
                        />
                    </Table.Cell>
                </Table.Row>
            )}
        </React.Fragment>
    )
}

const TeamProjectsTable = ({ month, day, teamId }) => {
    const { loading, data, error } = useQuery(
        LOAD_AI_USAGE_COST_TEAM_PROJECTS_QUERY,
        { variables: { month, day, teamId } }
    )

    if (loading) return <LoadingSpinner />
    if (error) {
        return <div>Error loading AI usage by project: {error.message}</div>
    }

    const projects = data?.adminPortal?.aiUsageCostTeamProjects || []

    return (
        <Table compact celled size="small">
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell width={1} />
                    <Table.HeaderCell>Project</Table.HeaderCell>
                    <Table.HeaderCell>Input Tokens</Table.HeaderCell>
                    <Table.HeaderCell>Output Tokens</Table.HeaderCell>
                    <Table.HeaderCell>Thinking Tokens</Table.HeaderCell>
                    <Table.HeaderCell>Cost</Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {projects.length === 0 && (
                    <Table.Row>
                        <Table.Cell colSpan={6}>No AI usage recorded.</Table.Cell>
                    </Table.Row>
                )}
                {projects.map((project) => (
                    <ProjectRow
                        key={project.projectId || 'no-project'}
                        project={project}
                        month={month}
                        day={day}
                        teamId={teamId}
                    />
                ))}
            </Table.Body>
        </Table>
    )
}

const TeamRow = ({ team, month, day }) => {
    const [expanded, setExpanded] = useState(false)

    return (
        <React.Fragment>
            <Table.Row>
                <Table.Cell collapsing>
                    <ExpandButton
                        expanded={expanded}
                        onClick={() => setExpanded(!expanded)}
                    />
                </Table.Cell>
                <Table.Cell>{team.teamName}</Table.Cell>
                <Table.Cell>{formatTokens(team.promptTokens)}</Table.Cell>
                <Table.Cell>{formatTokens(team.candidatesTokens)}</Table.Cell>
                <Table.Cell>{formatTokens(team.thoughtsTokens)}</Table.Cell>
                <Table.Cell>{formatCost(team.cost)}</Table.Cell>
            </Table.Row>
            {expanded && (
                <Table.Row>
                    <Table.Cell />
                    <Table.Cell colSpan={5}>
                        <TeamProjectsTable month={month} day={day} teamId={team.teamId} />
                    </Table.Cell>
                </Table.Row>
            )}
        </React.Fragment>
    )
}

const DayTeamsTable = ({ day }) => {
    const { loading, data, error } = useQuery(LOAD_AI_USAGE_COST_DAY_TEAMS_QUERY, {
        variables: { day },
    })

    if (loading) return <LoadingSpinner />
    if (error) {
        return <div>Error loading AI usage by team: {error.message}</div>
    }

    const teams = data?.adminPortal?.aiUsageCostDayTeams?.teams || []

    return (
        <Table compact celled size="small">
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell width={1} />
                    <Table.HeaderCell>Team</Table.HeaderCell>
                    <Table.HeaderCell>Input Tokens</Table.HeaderCell>
                    <Table.HeaderCell>Output Tokens</Table.HeaderCell>
                    <Table.HeaderCell>Thinking Tokens</Table.HeaderCell>
                    <Table.HeaderCell>Cost</Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {teams.length === 0 && (
                    <Table.Row>
                        <Table.Cell colSpan={6}>No AI usage recorded.</Table.Cell>
                    </Table.Row>
                )}
                {teams.map((team) => (
                    <TeamRow key={team.teamId} team={team} day={day} />
                ))}
            </Table.Body>
        </Table>
    )
}

const DayRow = ({ day }) => {
    const [expanded, setExpanded] = useState(false)

    return (
        <React.Fragment>
            <Table.Row>
                <Table.Cell collapsing>
                    <ExpandButton
                        expanded={expanded}
                        onClick={() => setExpanded(!expanded)}
                    />
                </Table.Cell>
                <Table.Cell>{formatDayLabel(day.date)}</Table.Cell>
                <Table.Cell>{formatTokens(day.promptTokens)}</Table.Cell>
                <Table.Cell>{formatTokens(day.candidatesTokens)}</Table.Cell>
                <Table.Cell>{formatTokens(day.thoughtsTokens)}</Table.Cell>
                <Table.Cell>{formatCost(day.cost)}</Table.Cell>
            </Table.Row>
            {expanded && (
                <Table.Row>
                    <Table.Cell />
                    <Table.Cell colSpan={5}>
                        <DayTeamsTable day={day.date} />
                    </Table.Cell>
                </Table.Row>
            )}
        </React.Fragment>
    )
}

const DailyCostTable = ({ month }) => {
    const { loading, data, error } = useQuery(LOAD_AI_USAGE_COST_DAILY_QUERY, {
        variables: { month },
        skip: !month,
    })

    if (loading) return <LoadingSpinner />
    if (error) {
        return <div>Error loading daily AI usage: {error.message}</div>
    }

    const days = data?.adminPortal?.aiUsageCostDaily || []
    const total = days.reduce(
        (acc, day) => ({
            promptTokens: acc.promptTokens + day.promptTokens,
            candidatesTokens: acc.candidatesTokens + day.candidatesTokens,
            thoughtsTokens: acc.thoughtsTokens + day.thoughtsTokens,
            cost: acc.cost + day.cost,
        }),
        { promptTokens: 0, candidatesTokens: 0, thoughtsTokens: 0, cost: 0 }
    )

    return (
        <Table celled striped>
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell width={1} />
                    <Table.HeaderCell>Day</Table.HeaderCell>
                    <Table.HeaderCell>Input Tokens</Table.HeaderCell>
                    <Table.HeaderCell>Output Tokens</Table.HeaderCell>
                    <Table.HeaderCell>Thinking Tokens</Table.HeaderCell>
                    <Table.HeaderCell>Cost</Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {days.length === 0 && (
                    <Table.Row>
                        <Table.Cell colSpan={6}>No AI usage recorded for this month.</Table.Cell>
                    </Table.Row>
                )}
                {days.map((day) => (
                    <DayRow key={day.date} day={day} />
                ))}
            </Table.Body>
            <Table.Footer>
                <Table.Row>
                    <Table.HeaderCell />
                    <Table.HeaderCell>Month Total</Table.HeaderCell>
                    <Table.HeaderCell>{formatTokens(total.promptTokens)}</Table.HeaderCell>
                    <Table.HeaderCell>{formatTokens(total.candidatesTokens)}</Table.HeaderCell>
                    <Table.HeaderCell>{formatTokens(total.thoughtsTokens)}</Table.HeaderCell>
                    <Table.HeaderCell>{formatCost(total.cost)}</Table.HeaderCell>
                </Table.Row>
            </Table.Footer>
        </Table>
    )
}

const AICost = () => {
    const [selectedMonth, setSelectedMonth] = useState(null)
    const [view, setView] = useState('monthly')

    const { loading, data, error } = useQuery(LOAD_AI_USAGE_COST_SUMMARY_QUERY, {
        variables: { month: selectedMonth },
    })

    const summary = data?.adminPortal?.aiUsageCostSummary

    useEffect(() => {
        if (summary?.month && !selectedMonth) {
            setSelectedMonth(summary.month)
        }
    }, [summary, selectedMonth])

    if (loading && !summary) return <LoadingSpinner />
    if (error) {
        return <div>Error loading AI cost summary: {error.message}</div>
    }

    const monthOptions = (summary?.availableMonths || []).map((month) => ({
        key: month,
        value: month,
        text: formatMonthLabel(month),
    }))

    const teams = summary?.teams || []
    const platformTotal = summary?.platformTotal || {
        promptTokens: 0,
        candidatesTokens: 0,
        thoughtsTokens: 0,
        cost: 0,
    }

    return (
        <Segment style={{ padding: '2.5rem', width: '100%' }}>
            <Grid>
                <Grid.Row>
                    <Grid.Column width={8}>
                        <h2>AI Cost</h2>
                    </Grid.Column>
                    <Grid.Column width={8} textAlign="right">
                        <Button.Group basic size="small" style={{ marginRight: '0.5rem' }}>
                            <Button
                                active={view === 'monthly'}
                                onClick={() => setView('monthly')}
                            >
                                By Team
                            </Button>
                            <Button
                                active={view === 'daily'}
                                onClick={() => setView('daily')}
                            >
                                By Day
                            </Button>
                        </Button.Group>
                        <Dropdown
                            selection
                            options={monthOptions}
                            value={summary?.month}
                            onChange={(e, { value }) => setSelectedMonth(value)}
                            placeholder="Select month"
                        />
                    </Grid.Column>
                </Grid.Row>
            </Grid>

            {view === 'daily' ? (
                <DailyCostTable month={summary?.month} />
            ) : (
                <Table celled striped>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell width={1} />
                            <Table.HeaderCell>Team</Table.HeaderCell>
                            <Table.HeaderCell>Input Tokens</Table.HeaderCell>
                            <Table.HeaderCell>Output Tokens</Table.HeaderCell>
                            <Table.HeaderCell>Thinking Tokens</Table.HeaderCell>
                            <Table.HeaderCell>Cost</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {teams.length === 0 && (
                            <Table.Row>
                                <Table.Cell colSpan={6}>
                                    No AI usage recorded for this month.
                                </Table.Cell>
                            </Table.Row>
                        )}
                        {teams.map((team) => (
                            <TeamRow key={team.teamId} team={team} month={summary?.month} />
                        ))}
                    </Table.Body>
                    <Table.Footer>
                        <Table.Row>
                            <Table.HeaderCell />
                            <Table.HeaderCell>Platform Total</Table.HeaderCell>
                            <Table.HeaderCell>
                                {formatTokens(platformTotal.promptTokens)}
                            </Table.HeaderCell>
                            <Table.HeaderCell>
                                {formatTokens(platformTotal.candidatesTokens)}
                            </Table.HeaderCell>
                            <Table.HeaderCell>
                                {formatTokens(platformTotal.thoughtsTokens)}
                            </Table.HeaderCell>
                            <Table.HeaderCell>
                                {formatCost(platformTotal.cost)}
                            </Table.HeaderCell>
                        </Table.Row>
                    </Table.Footer>
                </Table>
            )}
        </Segment>
    )
}

export default AICost
